import { useEffect, useState } from 'react';

import { trpc, TrpcOutputs } from '../../config/trpc';

export type RequestDocuments = TrpcOutputs['getDocumentsByRequestId'];

/**
 * A request's documents, kept live off the document subscriptions.
 *
 * The plain data hook — no form involved, so reach for this by default. [[useFormDocuments]] wraps
 * it for the cases where a react-hook-form field has to stay in step with the list.
 *
 * Creates matter as much as updates and deletes here: a document list built from a page
 * payload (a quote query, say) only learns about the other side's upload on the next
 * refetch, so an AWB the FP just added, or an SLI the forwarder just added, silently
 * doesn't show. Pass `initialDocuments` when the caller already has them, to render
 * immediately and still pick up everything that lands afterwards.
 */
export const useRequestDocuments = (
  requestId?: number,
  initialDocuments?: RequestDocuments,
): [RequestDocuments, boolean, string?] => {
  const [documents, setDocuments] = useState<RequestDocuments>(initialDocuments ?? []);

  // The booked and quote screens share one route across requests, so navigating between
  // two of them changes `requestId` without remounting. Reset during render rather than in
  // an effect: the previous request's documents must never reach the screen, where they'd
  // be counted as this shipment's HAWBs and offered up for editing and deletion.
  const [renderedRequestId, setRenderedRequestId] = useState(requestId);
  if (requestId !== renderedRequestId) {
    setRenderedRequestId(requestId);
    setDocuments(initialDocuments ?? []);
  }

  const { data, isLoading, error } = trpc.getDocumentsByRequestId.useQuery(
    { request_id: requestId },
    { enabled: !!requestId },
  );

  // Not `data?.length`: a request whose last document was deleted returns [], and
  // skipping that leaves the deleted rows on screen.
  useEffect(() => {
    if (data) setDocuments(data);
  }, [data]);

  trpc.onDocumentCreated.useSubscription(
    { requestId },
    {
      enabled: !!requestId,
      onData: (document) => {
        setDocuments((prev) => (prev.some((doc) => doc?.id === document.id) ? prev : [...prev, document]));
      },
      onError: (error) => {
        console.log('onDocumentCreated.error: ', error);
      },
    },
  );

  trpc.onDocumentUpdated.useSubscription(
    { requestId },
    {
      enabled: !!requestId,
      onData: (document) => {
        setDocuments((prev) => prev.map((doc) => (doc?.id === document.id ? { ...doc, ...document } : doc)));
      },
      onError: (error) => {
        console.log('onDocumentUpdated.error: ', error);
      },
    },
  );

  trpc.onDocumentDeleted.useSubscription(
    { requestId },
    {
      enabled: !!requestId,
      onData: (deletedId) => {
        setDocuments((prev) => prev.filter((doc) => doc?.id !== deletedId));
      },
      onError: (error) => {
        console.log('onDocumentDeleted.error: ', error);
      },
    },
  );

  return [documents, isLoading, error?.message];
};
